import { ISignalP1 } from "../signal/ISignalP1";
import { Pointer } from "./Pointer";
import { Signal } from "../signal/Signal";
import { ITouchMoveEvent } from "./ITouchMoveEvent";
import { BrowserWindow } from "../device/BrowserWindow";
import { ObjectUtils } from "../data/ObjectUtils";

// TODO implement this
export enum UpdateMode
{
	IMMEDIATE = 0,
	REQUEST_ANIMATION_FRAME = 1,
	MANUAL = 2
}

export interface IPointerDetectorConfig
{
	element                    : HTMLElement;
	maxPointers              ? : number;
	convertToViewportPixels  ? : boolean;
	dispatchIndividualEvents ? : boolean;
	dispatchMultiEvents      ? : boolean;
	autoEnable               ? : boolean;
	updateMode               ? : UpdateMode;
	useTiming                ? : boolean;
	// TODO preventDefault           ? : boolean;
}

/**
 * This class unified mouse and touch events to be handled uniformly as pointer events.
 * id will be 1 for mouse events, the rest will be used for touches (2, 3, ...).
 *
 * This dispatches a separate event for each finger even when touched simultaneously. This
 * could be considered to be configurable to dispatch just one event and have the number
 * of fingers in the event data.
 *
 * You can use multiple instances of this class on the same canvas, and call
 * stopImmediatePropagation() on the original event, or you can use the same
 * instance and set pointerEvent.stopped to true to stop propagation.
 *
 * No gesture recognition is implemented here (that can be done in subclasses).
 *
 * screenX: device pixels (not css pixels)!
 *
 * Notes:
 *  - touches: all
 *  - targetTouches: the ones that started out in the same node (where we added the listener to?)
 *  - changedTouches: the ones involved in the event
 *
 * resources:
 * http://www.polymer-project.org/platform/pointer-events.html
 * http://smus.com/mouse-touch-pointer/#
 * http://stackoverflow.com/questions/55677/how-do-i-get-the-coordinates-of-a-mouse-click-on-a-canvas-element
 * https://plus.google.com/u/0/115788095648461403871/posts/Ny6ZXuzWdN5
 * http://blogs.msdn.com/b/davrous/archive/2013/02/20/handling-touch-in-your-html5-apps-thanks-to-the-pointer-events-of-ie10-and-windows-8.aspx
 * https://developers.google.com/events/io/sessions/361772634
 *
 * @param element typically a HTMLCanvasElement but it can be anything that supports events.
 *
 * Consider:
 * - separating mouse / touch related code to external classes.
 * - optional requestAnimationFrame
 * - object pooling for events
 * - preventDefault
 * - simultaneous multi tap event
 * - using [] instead of localX, localY
 *
 */
export class PointerDetector
{

	public static defaultConfig: IPointerDetectorConfig =
	{
		element: null,
		maxPointers: 5,
		convertToViewportPixels: undefined,
		dispatchIndividualEvents: true,
		dispatchMultiEvents: false,
		autoEnable: true,
		updateMode: UpdateMode.IMMEDIATE,
		useTiming: false
	};

	public down: ISignalP1<Pointer>;
	public move: ISignalP1<Pointer>;
	public up  : ISignalP1<Pointer>;

	public multi : {
		down: ISignalP1<Pointer[]>;
		move: ISignalP1<Pointer[]>;
		up  : ISignalP1<Pointer[]>;
	};

	// TODO implement
	// dispatched when a move is occurring without a preceding down (eg. mousemove without mousedown)
	public hoverMove: ISignalP1<Pointer>;

	public pointers: {[id: number]: Pointer};
	public pointerArray: Pointer[];

	private _config: IPointerDetectorConfig;

	// cache
	private _localCoords: number[];

	constructor(config: IPointerDetectorConfig)
	{
		this._config = ObjectUtils.mergeConfig(PointerDetector.defaultConfig, config);

		this.pointers = {};
		this.pointerArray = [];

		this.down = new Signal();
		this.move = new Signal();
		this.up   = new Signal();

		if (this._config.dispatchMultiEvents)
		{
			this.multi = {
				down: new Signal(),
				move: new Signal(),
				up  : new Signal()
			};
		}

		// cache
		this._localCoords = [];

		if (this._config.autoEnable)
		{
			this.enable();
		}
	}

	// ===========================================================================================================
	// Enable, disable

	public enable()
	{
		var element = this._config.element;

		element.addEventListener('touchstart',  this.onTouchStart);
		element.addEventListener('mousedown',   this.onMouseDown);

		// todo add touchend during touchstart only, and on e.target, not the dom!
		// (otherwise target child element might receive the event and that might be removed
		// from the dom -> parent will never receive it -> will be stuck)
		// note: even if DOM target is removed from the dom, it sill receives the events

		element.addEventListener('touchmove',   this.onTouchMove);
		element.addEventListener('touchend',    this.onTouchEnd);
		element.addEventListener('touchcancel', this.onTouchCancel);
	}

	public disable()
	{
		var element = this._config.element;

		element.removeEventListener('touchstart',  this.onTouchStart);
		element.removeEventListener('mousedown',   this.onMouseDown);

		element.removeEventListener('touchmove',   this.onTouchMove);
		element.removeEventListener('touchend',    this.onTouchEnd);
		element.removeEventListener('touchcancel', this.onTouchCancel);
	}

	// ===========================================================================================================
	// Down

	private onTouchStart = (event: TouchEvent):void =>
	{
		// without this, onMouseDown would also fire afterwards
		event.preventDefault();

		var pointers:Pointer[] = [];

		for (var i: number = 0, ln = event.changedTouches.length; i < ln; ++i)
		{
			var touch = event.changedTouches[i];

			// identifier starts from 0 -> we add 2 (1 is for mouse)
			var pointer = this.onPointerDown(touch.identifier+2, touch, event);
			if (pointer)
			{
				pointers.push(pointer);
			}
		}

		if (this._config.dispatchMultiEvents)
		{
			this.multi.down.dispatch(pointers);
		}
	}

	private onMouseDown = (event: MouseEvent):void =>
	{
		event.preventDefault();

		// we only listen to move / up when mouse is down
		// need to add these on document (in case the mouse leaves the target dom)
		document.addEventListener('mousemove', this.onMouseMove);
		document.addEventListener('mouseup',   this.onMouseUp);

		this.onPointerDown(1, event, event);
	}

	private onPointerDown(id: number, eventData: ITouchMoveEvent, originalEvent: Event)
	{
		var maxPointers = this._config.maxPointers;
		if (maxPointers > -1 && this.pointersLength >= maxPointers)
		{
			// no more pointers allowed
			return null;
		}

		var pointer = this.createPointer(id, eventData, originalEvent);

		this.pointers[id] = pointer;
		this.pointerArray.push(pointer);

		if (this._config.dispatchIndividualEvents)
		{
			this.down.dispatch(pointer);
		}

		return pointer;
	}

	// ===========================================================================================================
	// Move

	private onMouseMove = (event: MouseEvent): void =>
	{
		event.preventDefault();

		this.onPointerMove(1, event, event);
	}

	private onTouchMove = (event: TouchEvent): void =>
	{
		// disable scrolling ?
		event.preventDefault();

		var pointers:Pointer[] = [];

		for (var i = 0, ln = event.changedTouches.length; i < ln; i++)
		{
			var touch = event.changedTouches[i];
			var pointer = this.onPointerMove(touch.identifier+2, touch, event);

			if (pointer)
			{
				pointers.push(pointer);
			}
		}

		if (this._config.dispatchMultiEvents)
		{
			this.multi.move.dispatch(pointers);
		}
	}

	private onPointerMove(id: number, eventData: ITouchMoveEvent, originalEvent: Event)
	{
		var pointer = this.pointers[id];

		if (!pointer)
		{
			return null;
		}

		var localCoords = this.getLocalCoords(eventData);

		var localX = localCoords[0];
		var localY = localCoords[1];

		pointer.dx = localX - pointer.localX;
		pointer.dy = localY - pointer.localY;
		pointer.offsetX = localX - pointer.startX;
		pointer.offsetY = localY - pointer.startY;
		pointer.localX = localX;
		pointer.localY = localY;
		pointer.originalEvent = originalEvent;

		if (this._config.dispatchIndividualEvents)
		{
			this.move.dispatch(pointer);
		}

		return pointer;
	}

	// ===========================================================================================================
	// Up

	private onMouseUp = (event: MouseEvent): void =>
	{
		event.preventDefault();

		document.removeEventListener('mousemove', this.onMouseMove);
		document.removeEventListener('mouseup',   this.onMouseUp);

		this.onPointerUp(1, event, event);
	}

	private onTouchEnd = (event: TouchEvent): void =>
	{
		this.onTouchFinish(event, false);
	}

	private onTouchCancel = (event: TouchEvent): void =>
	{
		this.onTouchFinish(event, true);
	}

	/**
	 * Called at touchend and touchcancel
	 */
	private onTouchFinish = (event: TouchEvent, canceled: boolean): void =>
	{
		event.preventDefault();

		var pointers: Pointer[] = [];

		for (var i = 0, ln = event.changedTouches.length; i < ln; ++i)
		{
			var touch = event.changedTouches[i];
			var pointer = this.onPointerUp(touch.identifier+2, touch, event);

			if (pointer)
			{
				pointer.canceled = canceled;
				pointers.push(pointer);
			}
		}

		if (this._config.dispatchMultiEvents)
		{
			this.multi.up.dispatch(pointers);
		}
	}

	private onPointerUp(id: number, eventData: ITouchMoveEvent, originalEvent: Event)
	{
		var pointer = this.pointers[id];

		if (!pointer)
		{
			return null;
		}

		// TODO try to remove from pointers as well
		var pointerArrayIndex = this.pointerArray.indexOf(pointer);
		if (pointerArrayIndex > -1)
		{
			this.pointerArray.splice(pointerArrayIndex, 1);
		}

		pointer.originalEvent = originalEvent;

		if (this._config.dispatchIndividualEvents)
		{
			this.up.dispatch(pointer);
		}

		return pointer;
	}

	// ===========================================================================================================
	// Pressed, moved data

	private createPointer(id: number, pointerData: ITouchMoveEvent, originalEvent: Event)
	{
		var localCoords = this.getLocalCoords(pointerData);

		var time = this._config.useTiming ? new Date().getTime() : 0;

		var pointer = new Pointer({
			id: id,
			localX: localCoords[0],
			localY: localCoords[1],
			time: time,
			originalEvent: originalEvent
		});

		return pointer;
	}

	/**
	 * This assumes proper support for pageX, pageY.
	 * Used by this class but it can be called externally with a Touch / MouseEvent.
	 * @returns {number[]}
	 */
	public getLocalCoords(pointerData: ITouchMoveEvent)
	{
		var clientRect = this._config.element.getBoundingClientRect();

		var left = clientRect.left + BrowserWindow.getScrollX();
		var top  = clientRect.top  + BrowserWindow.getScrollY();

		BrowserWindow.getScrollX();

		var localCoordX = pointerData.pageX - left;
		var localCoordY = pointerData.pageY - top;

		if (this._config.convertToViewportPixels)
		{
			// these are not rounded!
			var canvas  = <HTMLCanvasElement>this._config.element;
			localCoordX = localCoordX * canvas.width  / clientRect.width; // we could use canvas.clientWidth
			localCoordY = localCoordY * canvas.height / clientRect.height;
		}

		this._localCoords[0] = localCoordX;
		this._localCoords[1] = localCoordY;

		return this._localCoords;
	}

	// ===========================================================================================================
	// Getters

	public get pointersLength()
	{
		return this.pointerArray.length;
		//return Object.keys(this.pointers).length;
	}
}